import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useHotel } from "../context/HotelContext";
import LoadingGif from "../components/LoadingGif";

const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const ANIMAL_TYPES = [
  { key: "dog", labelKey: "AnimalDog", icon: "/icons/dog.svg" },
  { key: "cat", labelKey: "AnimalCat", icon: "/icons/cat.svg" },
  { key: "rabbit", labelKey: "AnimalRabbit", icon: "/icons/rabbit.svg" },
  { key: "bird", labelKey: "AnimalBird", icon: "/icons/bird.svg" },
  { key: "smallAnimal", labelKey: "SmallAnimal", icon: "/icons/hamster.svg" },
  { key: "reptile", labelKey: "AnimalReptile", icon: "/icons/turtle.svg" },
];

const HotelAdd_Information_1 = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { hotelData, loading, saveStep, fetchDraft } = useHotel();
  const [selectedAnimals, setSelectedAnimals] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id && !hotelData.animalTypes) {
      fetchDraft(id);
    }
  }, [id, hotelData.animalTypes, fetchDraft]);

  useEffect(() => {
    if (hotelData.animalTypes) {
      // Handle both nested and flat structures
      const animals = Array.isArray(hotelData.animalTypes)
        ? hotelData.animalTypes
        : hotelData.animalTypes.animalTypes || [];
      setSelectedAnimals(animals);
    }
  }, [hotelData.animalTypes]);

  const toggleAnimal = (key) => {
    setSelectedAnimals((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleNext = async () => {
    try {
      setIsSaving(true);
      await saveStep("animalTypes", selectedAnimals);
      navigate(`/hotel/add/info2/${id}`);
    } catch (err) {
      console.error("Failed to save step:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      await saveStep("animalTypes", selectedAnimals);
      navigate(`/hotel/onboarding`);
    } catch (err) {
      console.error("Failed to save step:", err);
    }
  };
  

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto", }}>
          {STEPS.map((step, index) => (
            <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: isMobile ? "20px" : "30px",
                    height: isMobile ? "20px" : "30px",
                    borderRadius: "50%",
                    backgroundColor: index === 0 ? "#31BACD" : "transparent",
                    border: index === 0 ? "3px solid #fff" : "2px solid #D1D1D1",
                    boxShadow: index === 0 ? "0 0 0 2px #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "13px",
                    fontWeight: index === 0 ? "600" : "400",
                    color: index === 0 ? "#000" : "#666",
                    textAlign: "center",
                    whiteSpace: "wrap",
                    width: isMobile ? "50px" : "100px",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    width: isMobile ? "30px" : "200px",
                    backgroundColor: "#D1D1D1",
                    mx: "auto",
                    mb: isMobile ? "18px" : "24px",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          {/* Heading */}
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%", mb: 1, flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: isMobile ? "20px" : "24px",
                  color: "#000",
                  mb: 1,
                }}
              >
                {t('HotelInfoStep1Title')}
              </Typography>
              <Typography
                sx={{
                  fontSize: isMobile ? "13px" : "14px",
                  color: "#666",
                  maxWidth: "600px",
                }}
              >
                {t('HotelInfoStep1Desc')}
              </Typography>
            </Box>
          </Box>

          {/* Animal type grid */}
          {loading ? (
            <LoadingGif />
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
                gap: 2,
                width: "100%",
                mt: 4,
              }}
            >
              {ANIMAL_TYPES.map((animal) => {
                const selected = selectedAnimals.includes(animal.key);
                return (
                  <Box
                    key={animal.key}
                    onClick={() => toggleAnimal(animal.key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      backgroundColor: "#FFFFFF",
                      border: selected ? "2px solid #31BACD" : "2px solid #D1D1D1",
                      borderRadius: "10px",
                      padding: "20px",
                      height: isMobile ? "70px" : "90px",
                      cursor: "pointer",
                      transition: "border-color 0.2s ease",
                    }}
                  >
                    <img src={animal.icon} alt={t(animal.labelKey)} style={{ width: "32px", height: "32px" }} />
                    <Typography sx={{ fontSize: isMobile ? "14px" : "16px", fontWeight: "500", color: "#000" }}>
                      {t(animal.labelKey)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={selectedAnimals.length === 0 || loading}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {loading ? <img src="/icons/loading.gif" alt="Loading" width="100" height="auto" /> : t('SaveAndLeave')}
            </Button>
            <Button
              variant="contained"
              disabled={selectedAnimals.length === 0 || isSaving}
              onClick={handleNext}
              sx={{
                backgroundColor: "#31BACD",
                color: "#fff",
                borderRadius: "10px",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                padding: "10px 32px",
                "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                "&:disabled": { opacity: 0.5, color: "#fff" },
              }}
            >
              {isSaving ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
            </Button>
          </Box>
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default HotelAdd_Information_1;

